import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { useParams } from 'react-router';
import ProfileActionButton from '../../molecules/Profile/ProfileActionButton';
import { useAppState } from '../../../providers/app-state/app-state-provider';
import { useAuthContext } from '../../../providers/auth-provider';
import BackingModal from '../../../pages/wholeme/molecules/backingModal/BackingModal';
import DonorboxModal from '../../molecules/DonorboxModal';
import { openInBrowser } from '../../../helpers/broswer';
import { gridItemStyle } from './ActionsGridStyles';
import { getBackButtonLabel, getButtonStyles } from './utils';
import appStrings, { SUPPORT_MAILTO } from '../../app_strings';
import PROFILE_PERMISSIONS from '../../../permissions/profile.permissions';

const {
  CAN_MEET,
  CAN_INVEST,
  CAN_BACK,
  CAN_PROPOSE,
} = PROFILE_PERMISSIONS;

// show ALL grid buttons to represent a unified user model. Just add another row with the team/groups counts. We'll show 6 grid items
const ActionsGrid = ({
  permissions, profile, refreshProfile,
}) => {
  const params = useParams();
  const subjectUserId = params?.userId || params?.id;
  const { user } = useAuthContext();
  const [backingModalOpen, setBackingModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { role, calendly_url: calendlyUrl } = profile;
  const subjectRole = role;
  const { platform } = useAppState();

  const {
    profile:
    {
      actionButtons: {
        messageAdmin, meet: meetLabel, invest: investLabel,
      },
    },
  } = appStrings;

  const accessorUserId = user?.id;

  const backingModalMeta = {
    subjectUserId: parseInt(subjectUserId, 10),
    profile,
    accessorUserId,
  };

  const handleAdminSupportClick = () => {
    window.location.href = SUPPORT_MAILTO;
  };

  const handleInvestClick = () => {
    setIsOpen(true);
  };
  const handleInvestClose = () => {
    setIsOpen(false);
  };

  const investModalMeta = {
    isOpen,
    handleClose: handleInvestClose,
    widgetSrc: `https://donorbox.org/embed/${profile?.donorboxId}?default_interval=m&hide_donation_meter=true&enable_auto_scroll=false`,
    widgetTitle: 'donorbox campaign',
    iframeOverrideStyle: {
      maxWidth: '500px',
      minWidth: '310px',
      height: '65vh',
      maxHeight: 'none! important',
    },
  };

  const handleOpenBackModal = () => {
    setBackingModalOpen(true);
  };

  const handleCloseBackModal = () => {
    setBackingModalOpen(false);
  };

  const handleOnSuccess = async () => {
    await refreshProfile({ loadingSpinner: false });
  };

  const handleCalendlyClick = async () => {
    if (platform?.isNativePlatform) {
      await openInBrowser(calendlyUrl);
    } else {
      window.open(calendlyUrl, '_blank');
    }
  };

  const getBackButtonMeta = (subjectProfile) => {
    const isBackingThisUser = subjectProfile?.relationshipStatus?.isBackingThisUser;
    const isBackingProposedThisUser = subjectProfile?.relationshipStatus?.isBackingThisUserProposed;
    const label = getBackButtonLabel(subjectProfile);
    const isDisabled = !isBackingThisUser
      && !isBackingProposedThisUser
      && (!permissions?.[CAN_BACK]?.allowed || !permissions?.[CAN_PROPOSE]?.allowed);
    const callback = label === 'Back' ? handleOpenBackModal : null;
    return {
      callback,
      isDisabled,
      label,
      isBackingThisUser,
    };
  };

  const getAffinityButtonMeta = (subjectProfile) => {
    const isDisabled = !permissions?.[CAN_INVEST]?.allowed || !subjectProfile?.donorboxId;
    return {
      callback: handleInvestClick,
      isDisabled,
      label: investLabel,
    };
  };

  const backButtonMeta = getBackButtonMeta(profile);
  const affinityButtonMeta = getAffinityButtonMeta(profile);

  if (subjectRole === 'admin') {
    return (
      <Grid container spacing={0.2}>
        <Grid sx={gridItemStyle} item xs={12}>
          <ProfileActionButton
            meta={{ callback: handleAdminSupportClick, label: messageAdmin }}
          />
        </Grid>
        <Grid sx={gridItemStyle} item xs={12}>
          <ProfileActionButton
            meta={{ callback: handleCalendlyClick, label: meetLabel }}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container rowSpacing={0.2} columnSpacing={1}>
        <Grid sx={gridItemStyle} item xs={6}>
          {/* this will be back button */}
          <ProfileActionButton
            buttonStylesOverride={getButtonStyles(backButtonMeta?.label)}
            meta={backButtonMeta}
            isDisabled={backButtonMeta?.isDisabled}
          />
        </Grid>
        <Grid sx={gridItemStyle} item xs={6}>
          {/* this will be invest button */}
          <ProfileActionButton
            buttonStylesOverride={getButtonStyles(affinityButtonMeta?.label)}
            meta={affinityButtonMeta}
            isDisabled={affinityButtonMeta?.isDisabled}
          />
        </Grid>
        <Grid sx={gridItemStyle} item xs={6}>
          {/* this will be meet button */}
          <ProfileActionButton
            meta={{ callback: handleCalendlyClick, label: meetLabel }}
            isDisabled={!calendlyUrl || !permissions?.[CAN_MEET]?.allowed}
          />
        </Grid>
      </Grid>
      <DonorboxModal {...investModalMeta} />
      <BackingModal
        meta={backingModalMeta}
        isOpen={backingModalOpen}
        handleClose={handleCloseBackModal}
        onSuccess={handleOnSuccess}
      />
    </>
  );
};

export default ActionsGrid;
